import { FC } from "react";

import { GetServerSideProps, InferGetServerSidePropsType as Infer } from "next";
import { useTranslation } from "next-i18next";

import { FullWidthVStack } from "@components/common";
import { ContextMessage } from "@components/Message";
import LayoutHead from "@components/PageHeader/LayoutHead";
import withPropsMiddleware from "@lib/withPropsMiddleware";
import { checkHasAuthCookie } from "@modules/auth";
import { Banners, loadBanners } from "@modules/banners";
import {loadFeatures, useFeatureData} from "@modules/features";
import Layout, { TWithLayout, LayoutContent } from "@modules/layout";
import { ForgotPasswordForm } from "@modules/password-recovery";
import { ROUTES } from "@utils/constants";
import getEnv from "@utils/getEnv";
import { IBanners } from "@modules/banners/models/banners";
import UserFeedbackForm from "@modules/userFeedback/components/UserFeedbackForm";
import { FEEDBACK_FORM } from "@modules/features/constants";
import { Desktop } from "@lib/media";

type TWithLocales = {
  defaultLocale: string;
  customLocales: string[];
  customLocale: string; // Change from string? to string
  features: string[] | null;
  banners: IBanners | null;
};

export const getServerSideProps: GetServerSideProps<TWithLocales> = withPropsMiddleware(
  async ({
    locale,
    req: {
      headers: { host },
      cookies,
    },
  }) => {
    // Redirect to landing page if user is logged in
    if (checkHasAuthCookie(cookies)) {
      return {
        redirect: {
          permanent: false,
          destination: ROUTES.LANDING,
        },
      };
    }
    const bannersZone = getEnv("NEXT_PUBLIC_BANNERS_FORGOT_PASSWORD_ZONE") as string;
    const banners = locale ? await loadBanners(bannersZone, locale, host!) : null;
    const features = locale ? await loadFeatures(locale, host) : null;

    return {
      props: { banners, features, defaultLocale: "", customLocales: [], customLocale: "" },
    };
  }
);

const ForgotPasswordPage: TWithLayout<FC<Infer<typeof getServerSideProps>>> = ({ banners }) => {
  const { t } = useTranslation();
  const features = useFeatureData();
  return (
    <>
      <LayoutHead title={t("pages-password_reset-title")} description={t("pages-password_reset-description")} />
      <LayoutContent>
        <FullWidthVStack spacing="1.5rem">
          <Banners bannersData={banners} isDesktopVisible isMobileVisible />
          <ContextMessage messageZone="forgotPassword" />
          <ForgotPasswordForm />
        </FullWidthVStack>
        { features?.includes(FEEDBACK_FORM) && <><Desktop><UserFeedbackForm /></Desktop></> }
      </LayoutContent>
    </>
  );
};

ForgotPasswordPage.Layout = <Layout hasRecaptcha hasWebchat={false} hasMobileWebchat={false} />;

export default ForgotPasswordPage;
